<template>
    <section class="donation-section" ref="startForm" :style="{'scroll-margin-top':headerHeight}">
        <div>
            <h3 class="donation-title">Donation</h3>
        </div>

        <div class="donation-wrap">
            <div v-if="!isSent" class="fields-wrap">
                <div class="field-box">
                    <template v-if="id > 0">
                        <label class="field-label">Campaign: <span style="font-weight: bold;">{{ currentCampaign?.name ?? '' }}</span></label>
                    </template>

                    <template v-else>
                        <label class="field-label">Select Campaign <span class="field-required">*</span></label>
                        <div class="field-element">
                            <select name="plan" style="width: 100%;" v-model="form.campaign_id">
                                <option v-for="campaign in campaigns" :key="campaign.id" :value="campaign.id">
                                    {{ campaign.name }}
                                </option>
                            </select>
                            <div class="error-messages">{{ messageFieldError('campaign_id') }}</div>
                        </div>
                    </template>
                </div>

                <div class="field-box campaign-description"
                     style="word-break: normal; overflow-wrap: break-word; white-space: normal;"
                     v-html="campaignDescription">
                </div>

                <div class="field-box">
                    <label class="field-label">First Name <span class="field-required">*</span></label>
                    <div class="field-element">
                        <input type="text" style="width: 100%;" v-model="form.first_name" />
                        <input type="hidden" name="secret_name" v-model="form.secret_name" />
                        <div class="error-messages">{{ messageFieldError('first_name') }}</div>
                    </div>
                </div>

                <div class="field-box">
                    <label class="field-label">Last Name <span class="field-required">*</span></label>
                    <div class="field-element">
                        <input type="text" style="width: 100%;" v-model="form.last_name" />
                        <div class="error-messages">{{ messageFieldError('last_name') }}</div>
                    </div>
                </div>

                <div class="field-box">
                    <label class="field-label">Organization</label>
                    <div class="field-element">
                        <input type="text" style="width: 100%;" v-model="form.organization_name" />
                        <div class="error-messages">{{ messageFieldError('organization_name') }}</div>
                    </div>
                </div>

                <div class="field-box">
                    <label class="field-label">Email <span class="field-required">*</span></label>
                    <div class="field-element">
                        <input type="text" style="width: 100%;" v-model="form.email" />
                        <div class="error-messages">{{ messageFieldError('email') }}</div>
                    </div>
                </div>

                <div class="field-box">
                    <label class="field-label">Phone</label>
                    <div class="field-element">
                        <input type="text" v-mask="phoneFormatMask" style="width: 100%;" v-model="form.phone" />
                        <div class="error-messages">{{ messageFieldError('phone') }}</div>
                    </div>
                </div>

                <div class="field-box">
                    <label class="field-label">Payment Method <span class="field-required">*</span></label>
                    <div>
                        <select v-if="availablePaymentMethods.length > 0" style="width: 100%;" v-model="form.payment_type">
                            <option v-for="method in availablePaymentMethods" :key="method.code" :value="method.code">
                                {{ method.name }}
                            </option>
                        </select>
                        <div class="error-messages">
                            <template v-if="availablePaymentMethods.length === 0">
                                <div style="padding-top: 8px;">Payment methods are missing or not configured. Contact your administrator</div>
                            </template>
                            <template >
                                {{ messageFieldError('payment_type') }}
                            </template>
                        </div>
                    </div>
                </div>

                <div class="field-box">
                    <label class="field-label">Donation Amount ({{ currency }}) <span class="field-required">*</span></label>
                    <div class="field-element">
                        <input type="number" min="0" step="0.01" style="width: 140px;" v-model="form.donation_amount" @focusout="checkMinimalAmount" />
                        <div class="error-messages">{{ messageFieldError('donation_amount') }}</div>
                    </div>
                </div>

                <div class="field-box">
                    <label class="field-label checkbox-label">
                        <input type="checkbox" v-model="form.is_anonymous" />
                        <span style="display: block; margin-left: 12px;">I would like to make this donation anonymously. Please do not publish my name.</span>
                    </label>
                    <div class="error-messages">{{ messageFieldError('is_anonymous') }}</div>
                </div>

                <div class="field-box">
                    <label class="field-label">Comment</label>
                    <div class="field-element">
                        <textarea rows="3" style="width: 100%; resize: none;" v-model="form.comment" />
                        <div class="error-messages">{{ messageFieldError('comment') }}</div>
                    </div>
                </div>

                <div class="field-box">
                    <label class="field-label">Address <span class="field-required">*</span></label>
                    <div class="field-element">
                        <input type="text" style="width: 100%;" v-model="form.address_1" />
                        <div class="error-messages">{{ messageFieldError('address_1') }}</div>
                    </div>
                </div>

                <div class="field-box">
                    <label class="field-label">Address 2</label>
                    <div class="field-element">
                        <input type="text" style="width: 100%;" v-model="form.address_2" />
                        <div class="error-messages">{{ messageFieldError('address_2') }}</div>
                    </div>
                </div>

                <div class="field-box">
                    <label class="field-label">City <span class="field-required">*</span></label>
                    <div class="field-element">
                        <input type="text" style="width: 100%;" v-model="form.city" />
                        <div class="error-messages">{{ messageFieldError('city') }}</div>
                    </div>
                </div>

                <div class="field-box">
                    <label class="field-label">State / Province <span class="field-required">*</span></label>
                    <div class="field-element">
                        <input type="text" style="width: 100%;" v-model="form.state" />
                        <div class="error-messages">{{ messageFieldError('state') }}</div>
                    </div>
                </div>

                <div class="field-box">
                    <label class="field-label">Postal Code <span class="field-required">*</span></label>
                    <div class="field-element">
                        <input type="text" style="width: 100%;" v-model="form.postal_code" />
                        <div class="error-messages">{{ messageFieldError('postal_code') }}</div>
                    </div>
                </div>

                <div class="field-box">
                    <label class="field-label">Country</label>
                    <div class="field-element">
                        <input type="text" style="width: 100%;" v-model="form.country" />
                        <div class="error-messages">{{ messageFieldError('country') }}</div>
                    </div>
                </div>

                <div style="text-align: right; padding: 8px 0;">
                    <hr style="margin-top: 0;" />
                    <button :disabled="isLoading" type="button" @click="sendForm">Submit</button>
                </div>
            </div>

            <template v-if="isSent">
                <div class="step-box">
                    <div class="transaction-number">Your order number is: <span class="number" @click="print">{{ result.transaction.number }}</span></div>
                    <div class="print-invoice" @click="print">Print Donation Order</div>

                    <template v-if="result.transaction.payment_type === 'cash'">
                        <div class="checkout-title">DO NOT MAIL CASH. THE DONATION WILL BE CREDITED WHEN CASH IS PROCESSED.</div>
                    </template>

                    <template v-if="result.transaction.payment_type === 'check'">
                        <div class="checkout-title">MAIL CHECK, THE DONATION WILL BE CREDITED WHEN CHECK IS PROCESSED.</div>
                    </template>

                    <template v-if="result.payment_type === 'pay-pal' || result.payment_type === 'stripe'">
                        <div class="checkout-title">THE DONATION WILL BE CREDITED WHEN PAYMENT IS PROCESSED</div>
                        <div class="pay-button">
                            <a :href="result.url" target="_blank" style="display: block;">
                                <img src="/images/pay-button.png" alt="Pay Now" style="width: 150px; height: auto;" />
                            </a>
                        </div>
                    </template>
                </div>
            </template>
        </div>
    </section>
</template>

<script>
import EditorComponent from "./EditorComponent.vue";
import notifications from "../mixins/notifications";
import { headerOffset } from "../mixins/headerOffset";
import helpers from "../mixins/helpers";
import publicSettings from "../mixins/publicSettings";

export default {
    name: "DonationFormComponent",
    mixins: [
        notifications,
        headerOffset,
        helpers,
        publicSettings,
    ],
    components: {
        EditorComponent
    },
    props: {
        id: {
            type: Number,
            default: 0
        },
        paymentMethods: {
            type: Object,
            default: {}
        }
    },
    data() {
        return {
            publicSettingsGroups: ['payments'],
            isLoading: false,
            isSent: false,
            campaigns: [],
            phoneFormatMask: '(###) ###-####',
            form: {
                campaign_id: 0,
                first_name: '',
                last_name: '',
                organization_name: '',
                email: '',
                phone: '',
                donation_amount: '0.00',
                address_1: '',
                address_2: '',
                country: '',
                city: '',
                state: '',
                postal_code: '',
                is_anonymous: false,
                comment: '',
                payment_type: '',
                secret_name: ''
            },
            result: {
                url: '',
                payment_type: '',
                transaction: {
                    id: 0,
                    number: ''
                }
            },
            campaignDescription: '',
        }
    },
    watch: {
        'form.campaign_id': function (value) {
            this.form.donation_amount = this.minimalDonationAmount.toFixed(2);
            this.setCampaignDescription();
        }
    },
    computed: {
        isAuth() {
            return window.safeData.isAuth && window.safeData.isAuth === '1';
        },
        currentCampaign() {
            return this.id > 0 ? this.campaigns.find(c => c.id === this.id) : null;
        },
        minimalDonationAmount() {
            return this.campaigns.find(c => c.id === this.form.campaign_id)?.minimal_amount ?? 0;
        },
        availablePaymentMethods() {
            const methods = [];

            for (const key in this.paymentMethods) {
                if (this.paymentMethods[key].enabled) {
                    methods.push({
                        code: key,
                        name: this.paymentMethods[key].name
                    });
                }
            }

            return methods;
        },
        currency() {
            return this.getPublicSetting('currency') ?? 'USD';
        },
    },
    mounted() {
        this.loadData();
        this.loadAuth();

        if (this.id > 0) {
            this.form.campaign_id = this.id;
        }
    },
    methods: {
        loadData() {
            this.isLoading = true;

            this.$api.get('donations/campaigns/' + this.id).then(response => {
                if (response.data) {
                    this.campaigns = response.data;

                    this.setMinimalAmount();
                    this.setCampaignDescription();
                }
            }).finally(() => {
                this.isLoading = false;
            });
        },
        loadAuth() {
            this.isLoading = true;

            this.$api.get('api/v1/auth').then(response => {
                if (response.data && response.data.is_auth) {
                    const data = response.data.user;
                    this.form.first_name = data.first_name;
                    this.form.last_name = data.last_name;
                    this.form.organization_name = data.organization_name;
                    this.form.phone = data.phone;
                    this.form.email = data.email;
                    this.form.address_1 = data.address_1;
                    this.form.address_2 = data.address_2;
                    this.form.postal_code = data.postal_code;
                    this.form.country = data.country;
                    this.form.state = data.state;
                    this.form.city = data.city;
                }
            }).finally(() => {
                this.isLoading = false;
            });
        },
        sendForm() {
            this.clearValidateErrors();

            this.isLoading = true;

            this.form.phone_length = this.phoneFormatMask.length;

            this.$api.post('donations/campaigns/' + this.form.campaign_id + '/donate', this.form).then(response => {
                if (response.data && response.data.status) {
                    if (response.data.status === 'success') {
                        this.isSent = true;
                        this.result = response.data;
                        this.clearForm();
                    }

                    if (response.data.status === 'error') {
                        this.setValidateErrors({
                            body: [response.data.message]
                        });
                    }
                }
            }).catch((error) => {
                if (error.response && error.response.data.errors) {
                    this.setValidateErrors(error.response.data.errors);
                }
            }).finally(() => {
                this.isLoading = false;
            });

            this.$refs.startForm.scrollIntoView({ behavior: 'smooth' });
        },
        print() {
            this.isLoading = true;
            this.$api.post('donations/print-invoice/' + this.result.transaction.id, {}, {
                responseType: 'blob'
            }).then(response => {
                if (response.data) {
                    const link = document.createElement('a');
                    const blob = new Blob([response.data], { type: 'application/pdf' });
                    link.href = window.URL.createObjectURL(blob);
                    window.open(link.href);
                    link.download = 'order-' + this.transaction.number + '.pdf';
                    document.body.appendChild(link);
                    // link.click();
                    document.body.removeChild(link);
                }
            }).finally(() => {
                this.isLoading = false;
            });
        },
        setMinimalAmount() {
            this.form.donation_amount = this.currentCampaign?.minimal_amount
                ? this.currentCampaign?.minimal_amount.toFixed(2)
                : '0.00';
        },
        setCampaignDescription() {
            this.campaignDescription = this.campaigns.find(c => c.id === this.form.campaign_id)?.description ?? '';
        },
        checkMinimalAmount() {
            if (+this.form.donation_amount < +this.minimalDonationAmount) {
                this.form.donation_amount = this.minimalDonationAmount.toFixed(2);
            } else if (+this.form.donation_amount > 999999) {
                this.form.donation_amount = '999999.00';
            }

            if (this.form.donation_amount.indexOf('.') === -1) {
                this.form.donation_amount += '.00';
            }
        },
        clearForm() {
            this.form = {
                campaign_id: 0,
                first_name: '',
                last_name: '',
                organization_name: '',
                email: '',
                phone: '',
                donation_amount: '0.00',
                address_1: '',
                address_2: '',
                country: '',
                city: '',
                state: '',
                postal_code: '',
                is_anonymous: false,
                comment: '',
                payment_type: '',
                secret_name: ''
            };

            if (this.isAuth) {
                this.loadAuth();
            }

            // this.result = {
            //     url: '',
            //     payment_type: '',
            //     transaction: {
            //         id: 0,
            //         number: ''
            //     }
            // };

            this.setMinimalAmount();
        }
    }
}
</script>

<style lang="scss">
body.modal-open {
    overflow: hidden;
}

.popup-content {
    .ck-editor__editable_inline {
        min-height: 300px!important;
    }
}
</style>

<style scoped lang="scss">
.donation-section {
    max-width: 600px;
    margin: 0 auto;
    width: 100%;
}

.donation-title {
    font-size: 1.5em;
    text-align: center;
    margin-bottom: 16px;
}

.field-box {
    display: flex;
    flex-direction: column;
    margin-bottom: 16px;
}

.field-label {
    display: block;
}

.checkbox-label {
    display: flex;
}

.field-required {
    color: red;
}

input:not([type=radio]):not([type=checkbox]), select {
    border: 1px solid #ccced1;
    padding: 0 8px;
    height: 34px!important;
}

.error-messages {
    color: red;
    font-size: 0.8em;
    width: 100%;
}

button {
    background: #ffffff;
    min-width: 100px;
    border: 1px solid #000000;
    border-radius: 4px;
    height: 34px!important;
}

button.send {
    border-color: green;
    color: green;
}

button.send:focus, button.send:focus-visible {
    border-color: green!important;
}

button:hover {
    background: #f0f0f0;
}

.popup {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 99999;
}

.popup-content {
    background-color: #fff;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    padding: 28px 20px 20px 20px;
    border-radius: 5px;
    overflow-y: auto;
    width: 100%;
    max-height: 90vh;
}

.checkout-title {
    font-size: 1.4em;
    margin-bottom: 16px;
    color: green;
    text-align: center;
}

.transaction-number {
    font-size: 1.2em;
    margin-bottom: 16px;
    text-align: center;
}

.number {
    cursor: pointer;
    font-weight: bold;
}

.print-invoice {
    font-size: 1.4em;
    margin-bottom: 16px;
    text-align: center;
    font-weight: bold;
    cursor: pointer;
}

.pay-title {
    font-size: 1.2em;
    margin-bottom: 16px;
    text-align: center;
}

.pay-button {
    text-align: center;
    margin-bottom: 16px;
}
</style>
